import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChild, ViewChildren, ChangeDetectorRef, computed, signal, AfterViewInit, NgZone, effect, untracked, OnDestroy } from '@angular/core';
import { SidebarService } from '@shared/services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { BadgeComponent } from '@shared/components/ui/badge/badge.component';
import { PermissionService } from '@shared/services/permission/permission.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { SidebarDTO, SidebarGroupDTO, SidebarCategoryDTO, SidebarModuleDTO } from '@models/sidebar.model';
import { CatalogLabelService } from '@shared/services/i18n/catalog-label.service';
import { SidebarSearchService, SidebarSearchResult, HighlightPart } from '@shared/services/navigation/sidebar-search.service';
import { NavLayoutService } from '@shared/services/nav-layout.service';
import { TranslocoModule } from '@jsverse/transloco';
import { ProsellLogoComponent } from '@shared/components/prosell-logo/prosell-logo.component';

@Component({
  selector: 'app-sidebar-menu',
  imports: [
    CommonModule,
    RouterModule,
    BadgeComponent,
    ProsellLogoComponent,
    NgOptimizedImage,
    TranslocoModule
  ],
  templateUrl: './app-sidebar-menu.component.html',
})
export class AppSidebarMenuComponent implements OnDestroy {
  perfilUsuario = computed(() => this.usuarioService.perfil());

  imgLogoIcon = 'assets/images/prosell/prosell-icon.webp';

  openSubmenu: string | null = null;
  subMenuHeights: Record<string, number> = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  // ── Buscador del sidebar ───────────────────────────────────────────────────
  // Filtro en vivo del catálogo de navegación. Al escribir, el árbol acordeón se
  // reemplaza por una lista PLANA de módulos coincidentes con su categoría como
  // contexto (patrón "quick filter" tipo command-palette), lo que evita tocar la
  // lógica de alturas del acordeón (que solo admite un submenú abierto a la vez).
  searchQuery = signal('');
  /** Índice del resultado resaltado para navegación con teclado (↑/↓ + Enter). */
  activeResultIndex = signal(0);
  @ViewChild('sidebarSearchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  /** Última lista de resultados renderizada; la reusan los handlers de teclado. */
  private lastResults: SidebarSearchResult[] = [];
  /** Pista del atajo según la plataforma (⌘K en Mac, Ctrl K en el resto). */
  readonly shortcutHint = /mac|iphone|ipad/i.test(navigator.userAgent)
    ? '⌘ K'
    : 'Ctrl K';

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  /**
   * Computed signal que indica si el menú del header está abierto
   * Usado para ajustar dinámicamente la posición del sidebar
   */
  isHeaderMenuOpen = computed(() => this.sidebarService.headerMenuOpen());

  private subscription: Subscription = new Subscription();
  sidebarData: SidebarDTO | null = null;
  private lastCompanyId: number | null = null;
  private lastRoleId: number | null = null;

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private usuarioService: UsuarioService,
    private permissionService: PermissionService,
    private companyContext: CompanyContextService,
    private ngZone: NgZone,
    private catalogLabel: CatalogLabelService,
    private sidebarSearch: SidebarSearchService,
    private navLayout: NavLayoutService
  ) {
    // Cargar sidebar cuando cambie el perfil O la empresa seleccionada.
    // Solo limpia caché si el companyId o roleId cambió respecto a la carga anterior,
    // evitando HTTP duplicados en la carga inicial (F5).
    effect(() => {
      const perfil = this.usuarioService.perfil();
      const companyId = this.companyContext.companyId();
      if (perfil?.role?.id && companyId !== null) {
        const roleId = perfil.role.id;
        // untracked: clearSidebarCache lee/escribe el signal sidebarCache.
        // Sin untracked, el effect se registraría como dependiente de ese signal
        // y se re-dispararía en bucle infinito.
        untracked(() => {
          const changed = companyId !== this.lastCompanyId || roleId !== this.lastRoleId;
          if (changed) {
            this.permissionService.clearSidebarCache(roleId);
            this.lastCompanyId = companyId;
            this.lastRoleId = roleId;
          }
          this.loadModuleToSidebar();
        });
      }
    });

    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
  }

  ngOnInit() {
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            this.cdr.detectChanges();
          }
        }
      )
    );

    // Suscribirse a solicitudes de expansión del sidebar
    this.subscription.add(
      this.sidebarService.expandForUrl$.subscribe(url => {
        this.setActiveMenuFromRoute(url);
      })
    );

    this.setActiveMenuFromRoute(this.router.url);

    // Listeners globales (resize + atajo de teclado del buscador). Se registran
    // fuera de Zone.js para no disparar change-detection en cada evento; solo se
    // reentra en la zona cuando realmente hay que reaccionar.
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onWindowResize);
      window.addEventListener('keydown', this.onGlobalKeydown);
    });
  }

  ngAfterViewInit() {
    this.subMenuRefs.changes.subscribe(() => {
      this.updateSubmenuHeights();
    });

    this.updateSubmenuHeights();
  }

  updateSubmenuHeights() {
    if (!this.openSubmenu) return;
    const container = document.getElementById(this.openSubmenu);
    if (!container) return;
    // NO usar container.scrollHeight para calcular la altura cuando el contenido se reduce.
    //
    // Por definicion del DOM: scrollHeight = max(clientHeight, contentHeight).
    // Cuando el <div> tiene height:250px (via ngStyle) y el contenido se reduce a 200px,
    // scrollHeight devuelve 250 (el valor antiguo), por lo que el height nunca se actualiza.
    // Esto afecta al caso "quitar permiso" (contraccion), no al caso "dar permiso"
    // (expansion), donde contentHeight > clientHeight y scrollHeight si refleja el cambio.
    //
    // Solucion: medir el <ul> hijo directo. Al carecer de height fija, su offsetHeight
    // siempre refleja la altura real del contenido (suma de <li> renderizados).
    // Se suma el margin-top del <ul> (clase mt-2) para preservar el espaciado superior.
    const innerList = container.querySelector<HTMLElement>(':scope > ul');
    if (!innerList) return;
    const marginTop = parseFloat(getComputedStyle(innerList).marginTop) || 0;
    this.subMenuHeights[this.openSubmenu] = innerList.offsetHeight + marginTop;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    // Limpiar listeners globales
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('keydown', this.onGlobalKeydown);
  }

  /**
   * Maneja el evento resize de la ventana para actualizar los estilos del sidebar
   * Se ejecuta fuera de la zona de Angular para mejor performance
   */
  private onWindowResize = () => {
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
  };

  isActive(path: string): boolean {
    return this.router.isActive(path, { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' });
  }

  /**
   * Antes la consulta del backend excluía por completo los módulos con status=0 (ocultos del
   * menú), lo que también los borraba de la lista de permisos que usa checkModulePermission() —
   * un módulo "plegado" (p. ej. Ofertas, Colecciones) quedaba sin ningún permiso consultable, aun
   * con canCreate/canEdit ya otorgados. Ahora el backend siempre los incluye (para que el permiso
   * exista) y este filtro es el único lugar que decide qué se pinta en el menú visual.
   */
  modulosVisibles(modules: SidebarModuleDTO[]): SidebarModuleDTO[] {
    return modules.filter(m => m.status === 1);
  }

  // pa generar IDs únicos de grupo y categoría
  getCategoryId(groupId: number, categoryId: number): string {
    return `group-${groupId}-category-${categoryId}`;
  }

  // ── i18n de etiquetas del sidebar (catálogo del sistema) ───────────────────
  // El catálogo (grupos/categorías/módulos) son datos de BD; su IDENTIDAD (url/nombre) no se
  // traduce, su PRESENTACIÓN sí. La resolución vive en CatalogLabelService (compartida con la
  // vista de gestión de Módulos para no duplicar lógica). Reacciona al idioma porque Transloco
  // dispara CD y estos métodos se reevalúan.
  groupLabel(g: SidebarGroupDTO): string {
    return this.catalogLabel.group(g.name);
  }

  categoryLabel(c: SidebarCategoryDTO): string {
    return this.catalogLabel.category(c.name, c.url);
  }

  moduleLabel(m: SidebarModuleDTO): string {
    return this.catalogLabel.module(m.name, m.url);
  }

  toggleSubmenu(key: string) {
    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      return;
    }
    this.openSubmenu = key;

    setTimeout(() => {
      const el = document.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
      }
    }, 0);
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe(expanded => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    if (!this.sidebarData) return;

    // Buscar la categoría que contiene el módulo activo
    this.sidebarData.groups.forEach(group => {
      group.categories.forEach(category => {
        const hasActiveModule = category.modules.some(module =>  this.router.isActive(module.url, {
          paths: 'subset',
          queryParams: 'ignored',
          fragment: 'ignored',
          matrixParams: 'ignored',
        }));
        
        if (hasActiveModule) {
          const categoryId = this.getCategoryId(group.id, category.id);
          this.openSubmenu = categoryId;
          
          setTimeout(() => {
            this.cdr.detectChanges();
            this.updateSubmenuHeights();
          }, 0);
        }
      });
    });
  }

  /**
   * Calcula los estilos dinámicos del sidebar según el tamaño de pantalla
   * En pantallas >= 1280px (xl): posición normal (top: 0, height: 100vh)
   * En pantallas < 1280px: el cajón ocupa todo el alto (top: 0, height: 100vh) y
   *   reserva la altura del header como `padding-top`, para que el menú se muestre
   *   completo bajo la barra superior sticky sin que el header lo coma (el fondo/
   *   borde del cajón corre a full-height detrás del header). Se reservan 75px
   *   (un poco más que el alto del header para que el menú no quede pegado); si su
   *   menú está desplegado es más alto, así que se descuenta esa altura mayor.
   */
  getSidebarStyle() {
    const isXlScreen = window.innerWidth >= 1280;

    // En pantallas grandes (xl+), mantener posición normal siempre
    if (isXlScreen) {
      return {};
    }

    // En pantallas pequeñas, reservar la altura del header como padding superior.
    const headerH = this.isHeaderMenuOpen()
      ? 'var(--header-expanded-height, 140px)'
      : '75px';

    return {
      'top': '0',
      'height': '100vh',
      'padding-top': headerH
    };
  }

  onSubmenuClick() {
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }  

  // ── Buscador del sidebar ───────────────────────────────────────────────────

  /** ¿Hay una búsqueda activa? Alterna entre árbol acordeón y lista de resultados. */
  isSearching(): boolean {
    return this.searchQuery().trim().length > 0;
  }

  onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.activeResultIndex.set(0);
  }

  /** Resetea el estado de búsqueda (sin tocar el foco). */
  clearSearch() {
    this.searchQuery.set('');
    this.activeResultIndex.set(0);
    this.lastResults = [];
  }

  /** Limpia y devuelve el foco al input (botón «×» y tecla Esc). */
  clearSearchAndFocus() {
    this.clearSearch();
    this.searchInputRef?.nativeElement.focus();
  }

  /**
   * Recorre el árbol (respetando el idioma activo) y devuelve los módulos coincidentes.
   * Delega en {@link SidebarSearchService} (lógica compartida con la barra superior).
   */
  searchResults(): SidebarSearchResult[] {
    this.lastResults = this.sidebarSearch.search(this.sidebarData, this.searchQuery());
    return this.lastResults;
  }

  /** Segmentos {texto, match} para resaltar la porción coincidente de una etiqueta. */
  highlightParts(text: string): HighlightPart[] {
    return this.sidebarSearch.highlightParts(text, this.searchQuery());
  }

  /** Navegación con teclado dentro del input de búsqueda (↑/↓, Enter, Esc). */
  onSearchKeydown(event: KeyboardEvent) {
    const results = this.lastResults;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (results.length) {
          this.activeResultIndex.update(i => Math.min(i + 1, results.length - 1));
          this.scrollActiveResultIntoView();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (results.length) {
          this.activeResultIndex.update(i => Math.max(i - 1, 0));
          this.scrollActiveResultIntoView();
        }
        break;
      case 'Enter': {
        event.preventDefault();
        const selected = results[this.activeResultIndex()];
        if (selected) this.goToResult(selected);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.clearSearchAndFocus();
        break;
    }
  }

  /**
   * Clic sobre un resultado: el [routerLink] del ancla ya navega, aquí solo
   * limpiamos la búsqueda y cerramos el cajón en móvil.
   */
  onResultClick() {
    this.clearSearch();
    this.onSubmenuClick();
  }

  /** Navegación por teclado (Enter): navega explícitamente y limpia la búsqueda. */
  goToResult(result: SidebarSearchResult) {
    this.router.navigateByUrl(result.module.url);
    this.clearSearch();
    this.onSubmenuClick();
  }

  private scrollActiveResultIntoView() {
    setTimeout(() => {
      const el = document.getElementById('sb-search-result-' + this.activeResultIndex());
      el?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }

  /**
   * Atajo global Ctrl/⌘+K: expande el sidebar (si está colapsado o cerrado en móvil)
   * y enfoca el buscador. Estándar de la industria para "ir a / buscar".
   */
  private onGlobalKeydown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
      // No actuar cuando el sidebar NO es la superficie de navegación activa: en escritorio
      // con disposición 'topnav' el sidebar está oculto y es la barra superior quien gestiona
      // el atajo. En móvil (< xl) el sidebar siempre actúa como cajón, sea cual sea la disposición.
      if (this.navLayout.mode() === 'topnav' && window.innerWidth >= 1280) return;
      event.preventDefault();
      this.ngZone.run(() => this.focusSearch());
    }
  };

  private focusSearch() {
    if (window.innerWidth < 1280) {
      this.sidebarService.setMobileOpen(true);
    } else {
      this.sidebarService.setExpanded(true);
    }
    // Esperar a que el input exista/sea visible tras expandir antes de enfocar.
    setTimeout(() => this.searchInputRef?.nativeElement.focus(), 50);
  }

  loadModuleToSidebar() {
    const perfil = this.perfilUsuario();
    const roleId = perfil?.role?.id;

    if (roleId) {
      this.permissionService.getVisibleSidebarForRoleCached(roleId)
        .subscribe({
          next: (data) => {
            this.sidebarData = data;
            this.cdr.detectChanges();
            this.setActiveMenuFromRoute(this.router.url);
            // Recalcular la altura del submenu abierto despues de actualizar sidebarData.
            // Se necesita porque:
            //   1. subMenuHeights guarda la altura calculada cuando se abrio el submenu.
            //   2. Si la lista de modulos cambia (permisos cambiados), el contenido del
            //      DOM cambia pero subMenuHeights mantiene el valor anterior.
            //   3. setActiveMenuFromRoute solo recalcula si la ruta actual esta en el sidebar;
            //      si el usuario esta en una pagina fuera del sidebar (ej. /permisos-editar/123),
            //      updateSubmenuHeights nunca se llama desde ahi.
            // El setTimeout garantiza que el event loop procesa el render de detectChanges
            // antes de que leamos scrollHeight, asegurando que el DOM tiene la nueva lista.
            // scrollHeight sobre overflow:hidden retorna la altura intrinseca del contenido,
            // independientemente del valor CSS de height, por lo que mide correctamente
            // los nuevos modulos sin necesidad de resetear subMenuHeights a cero.
            setTimeout(() => this.updateSubmenuHeights(), 0);
          },
          error: (err) => console.error('Error loading sidebar:', err)
        });
    }
  }
}
